import React, { useState, useEffect, useRef, useMemo } from 'react';

interface PhoneInputProps {
  label?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

interface Country {
  code: string;
  name: string;
  flag: string;
  iso: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const countries: Country[] = [
    { code: '+371', name: 'Latvia', flag: '🇱🇻', iso: 'LV' },
    { code: '+372', name: 'Estonia', flag: '🇪🇪', iso: 'EE' },
    { code: '+370', name: 'Lithuania', flag: '🇱🇹', iso: 'LT' },
    { code: '+358', name: 'Finland', flag: '🇫🇮', iso: 'FI' },
    { code: '+46', name: 'Sweden', flag: '🇸🇪', iso: 'SE' },
    { code: '+47', name: 'Norway', flag: '🇳🇴', iso: 'NO' },
    { code: '+45', name: 'Denmark', flag: '🇩🇰', iso: 'DK' },
    { code: '+49', name: 'Germany', flag: '🇩🇪', iso: 'DE' },
    { code: '+48', name: 'Poland', flag: '🇵🇱', iso: 'PL' },
    { code: '+7', name: 'Russia', flag: '🇷🇺', iso: 'RU' },
    { code: '+1', name: 'United States', flag: '🇺🇸', iso: 'US' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧', iso: 'GB' },
    { code: '+33', name: 'France', flag: '🇫🇷', iso: 'FR' },
    { code: '+39', name: 'Italy', flag: '🇮🇹', iso: 'IT' },
    { code: '+34', name: 'Spain', flag: '🇪🇸', iso: 'ES' },
  ];

  // Parse the current value to extract country code and phone number
  const { countryCode, phoneNumber } = useMemo(() => {
    if (!value || value.trim() === '') {
      return { countryCode: '+371', phoneNumber: '' };
    }

    // Sort countries by code length (longest first) to match correctly
    const sortedCountries = [...countries].sort((a, b) => b.code.length - a.code.length);
    const country = sortedCountries.find(c => value.startsWith(c.code));

    if (country) {
      const phoneNumber = value.substring(country.code.length);
      return {
        countryCode: country.code,
        phoneNumber: phoneNumber,
      };
    }

    // Default to Latvia if no country code found
    return { countryCode: '+371', phoneNumber: value };
  }, [value]);

  const handleCountryChange = (newCountryCode: string) => {
    const newValue = newCountryCode + phoneNumber;
    const syntheticEvent = {
      target: { name: name || '', value: newValue },
      currentTarget: { name: name || '', value: newValue }
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    setIsOpen(false);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const newPhoneNumber = inputValue.replace(/\D/g, '');
    const newValue = countryCode + newPhoneNumber;

    const syntheticEvent = {
      target: { name: name || '', value: newValue },
      currentTarget: { name: name || '', value: newValue }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const selectedCountry = countries.find(country => country.code === countryCode);

  return (
    <div className={`space-y-2 ${label ? 'mt-6' : ''} ${className}`}>
      {label && (
        <label className="block text-base font-semibold text-gray-700 mb-0.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex">
        {/* Container with unified border */}
        <div className={`
          flex w-full rounded-10 border transition-colors duration-200
          ${error ? 'border-red-500' : 'border-gray-300'}
          ${isFocused ? 'ring-2 ring-primary-500 border-transparent' : ''}
          ${error && isFocused ? 'ring-red-500' : ''}
          ${disabled ? 'bg-gray-100' : 'bg-white'}
        `}>
          {/* Country Code Dropdown */}
          <div className="w-28 relative z-10" ref={selectRef}>
            <button
              type="button"
              className={`
                relative w-full bg-transparent border-0 pl-2 pr-8 py-2.5 text-left 
                cursor-pointer focus:outline-none h-10 rounded-l-10
                ${!selectedCountry ? 'text-gray-400' : 'text-gray-900'}
                ${disabled ? 'cursor-not-allowed' : ''}
                transition duration-150 ease-in-out
              `}
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              <span className="block truncate">
                {selectedCountry ? `${selectedCountry.iso} ${selectedCountry.code}` : 'Select'}
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>

            {isOpen && !disabled && (
              <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-10 py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-gray-200">
                <ul tabIndex={-1} role="listbox">
                  {countries.map((country) => (
                    <li
                      key={country.code}
                      className={`
                        cursor-pointer select-none relative py-2 pl-2 pr-8 
                        ${country.code === countryCode ? 'text-white bg-primary-500' : 'text-gray-900 hover:bg-gray-100'}
                      `}
                      role="option"
                      aria-selected={country.code === countryCode}
                      onClick={() => handleCountryChange(country.code)}
                    >
                      <span className="block truncate">
                        {country.iso} {country.code}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Phone Number Input */}
          <div className="flex-1">
            <input
              ref={inputRef}
              type="tel"
              name={name}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder || "22222222"}
              required={required}
              maxLength={8}
              disabled={disabled}
              className={`
                block w-full bg-transparent border-0 rounded-r-10
                pl-4 pr-2.5 py-2.5 h-10
                focus:outline-none
                ${error ? 'bg-red-50' : 'bg-transparent'} 
                transition duration-150 ease-in-out
                placeholder:text-gray-400
                ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
                text-gray-900
              `}
            />
          </div>
        </div>
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default PhoneInput;
